import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import BoonLogo from '../../../assets/images/boon-logo.svg';
import BoonViewIcon from '../../../assets/images/BoonViewIcon.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/info.svg';
import { FormikTextInput } from '../../../components/FormikTextInput/FormikTextInput';
import NavigationBar from '../../../components/NavigationBar/NavigationBar';
import { ReferralFlowStepper } from '../../../components/ReferralFlowStepper/ReferralFlowStepper';
import { ReferralProcess } from '../../../components/ReferralProcess/ReferralProcess';
import { ReferralProcessModal } from '../../../components/ReferralProcess/ReferralProcessModal';
import ButtonWithIcon from '../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import { FormikPhoneInput } from '../../../components/shared/FormikPhoneInput/FormikPhoneInput';
import Header from '../../../components/shared/Header/Header';
import { useReferralFlowContext } from '../../../contexts/ReferralFlowContext';
import { IsMobile } from '../../../helpers/IsMobile';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

import styles from './PersonalInfo.module.scss';

const createValidationSchema = (t) =>
  Yup.object({
    firstName: Yup.string().required(t('validation:name.firstNameRequired')),
    lastName: Yup.string().required(t('validation:name.lastNameRequired')),
    email: Yup.string().email(t('validation:email.invalid')).required(t('validation:email.pleaseEnter')),
  });

export const PersonalInfo = () => {
  const { t } = useTranslation('common');
  const validationSchema = createValidationSchema(t);
  const { isAuthenticated } = useCurrentUser();
  const { personalInfo, setPersonalInfo, setCurrentStep } = useReferralFlowContext();
  const [showReferralProcessModal, setShowReferralProcessModal] = useState(false);
  const { companyName } = useParams();
  const isMobile = IsMobile();

  useEffect(() => {
    if (isAuthenticated) {
      setCurrentStep(2);
    }
  }, [isAuthenticated, setCurrentStep]);

  return (
    <div className="flex w-full">
      {isAuthenticated && !companyName && <NavigationBar />}
      <div className={styles.outerContainer}>
        <Header
          isPublic={!isAuthenticated || companyName}
          title={t('referral')}
          backButton={isAuthenticated && !companyName ? t('back') : ''}
          logo={isMobile ? BoonViewIcon : BoonLogo}
          logoClassName={isMobile ? 'w-full h-full' : 'w-full h-[20px] rounded-none'}
        />
        <div className={styles.personalInfoContainer}>
          <ReferralProcess />
          <div className={styles.container}>
            <ReferralFlowStepper currentStep={1} />

            <div className={styles.formSection}>
              <div className={styles.formHeaderContainer}>
                <div className={styles.formHeader}>
                  <h2>{t('referral:yourInfo')}</h2>
                  {/* <p>
                  Already have an account? <a href="/login">Log In</a>
                </p> */}
                </div>
                <InfoIcon className={styles.infoIcon} onClick={() => setShowReferralProcessModal(true)} />
              </div>

              <Formik
                initialValues={{
                  firstName: personalInfo?.firstName || '',
                  lastName: personalInfo?.lastName || '',
                  email: personalInfo?.email || '',
                  phone: personalInfo?.phone || '',
                }}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                  setPersonalInfo(values);
                  setCurrentStep(2);
                }}
              >
                {() => (
                  <Form>
                    <div className={styles.form}>
                      <div className={styles.row}>
                        <FormikTextInput
                          data-testid="personal-first-name"
                          name="firstName"
                          label={t('labels.yourName')}
                          placeholder={t('placeholders.firstName')}
                          isRequired
                        />
                        <div className="mt-[27px]">
                          <FormikTextInput
                            data-testid="personal-last-name"
                            name="lastName"
                            label={t('labels.yourName')}
                            placeholder={t('placeholders.lastName')}
                            shouldDisplayLabel={false}
                          />
                        </div>
                      </div>

                      <FormikTextInput
                        data-testid="personal-email"
                        name="email"
                        type="email"
                        label={t('labels.emailAddress')}
                        placeholder={t('placeholders.enterEmailAddress')}
                        isRequired
                      />
                      <FormikPhoneInput
                        data-testid="personal-phone"
                        name="phone"
                        label={t('labels.phoneNumber')}
                        placeholder={t('placeholders.phoneNumber')}
                        isOptional
                      />
                    </div>
                    <div className={styles.nextButtonContainer}>
                      <ButtonWithIcon
                        data-testid="next-button"
                        type="submit"
                        variant="primary-36"
                        text={t('next')}
                        textClassName="text-white"
                        containerClass="w-[142px] self-end"
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
      <ReferralProcessModal show={showReferralProcessModal} onClose={() => setShowReferralProcessModal(false)} />
    </div>
  );
};
